// src/pages/PlanUpgrade.tsx
import React, { useState, useEffect } from 'react';
import { Star, Rocket, Gem, Check, Zap, Shield, TrendingUp, Sparkles, X, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/auth/hooks';
import { createCheckoutSessionUpgrade } from '../lib/supabase/StripePlanUpgrade';
import { supabase } from '../lib/supabase';
import { SkeletonLoader } from '../components/ui/SkeletonLoader';
import toast from 'react-hot-toast';

/* -------- Types -------- */
type PageTransitionEvent = Event & { persisted?: boolean };

interface Plan {
  id: string;
  plan_key: string;
  code: string;
  name: string;
  description: string;
  monthly_price: number;
  yearly_price: number;
  stripe_price_id_monthly: string;
  stripe_price_id_yearly: string;
  features: any; // jsonb
  limits: any; // jsonb
  ai_tokens_limit: number | null;
  ai_display_limit: number | null;
  is_active: boolean;
}

/* -------- UI helpers -------- */
const planIcons: Record<string, React.ReactNode> = {
  basic: <Star className="w-6 h-6" />,
  medium: <Rocket className="w-6 h-6" />,
  vip: <Gem className="w-6 h-6" />
};

const planColors: Record<string, { from: string; to: string; accent: string; text: string }> = {
  basic: {
    from: 'from-blue-50',
    to: 'to-blue-100',
    accent: 'bg-blue-600',
    text: 'text-blue-600'
  },
  medium: {
    from: 'from-purple-50',
    to: 'to-purple-100',
    accent: 'bg-purple-600',
    text: 'text-purple-600'
  },
  vip: {
    from: 'from-amber-50',
    to: 'to-amber-100',
    accent: 'bg-gradient-to-r from-amber-500 to-orange-500',
    text: 'text-amber-600'
  }
};

/* -------- Confirm Modal -------- */
function ConfirmUpgradeModal({
  open,
  onClose,
  onConfirm,
  plan,
  isYearly,
  currentPlanKey,
  processing
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  plan: Plan | null;
  isYearly: boolean;
  currentPlanKey: string | null;
  processing: boolean;
}) {
  if (!plan) return null;

  const displayPrice = isYearly ? plan.yearly_price / 12 : plan.monthly_price;
  const cycleText = isYearly ? 'שנתי (מחיר ממוצע חודשי)' : 'חודשי';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />

          {/* Card */}
          <motion.div
            className="relative z-[81] w-[90vw] max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
          >
            <button
              onClick={onClose}
              className="absolute left-4 top-4 rounded-full p-1 text-gray-500 hover:bg-gray-100"
              aria-label="סגור"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mb-2 flex items-center gap-3">
              <div className="rounded-xl bg-gray-900 p-3 text-white">
                {planIcons[plan.plan_key] || <Star className="h-6 w-6" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">אישור שדרוג</h2>
                <p className="text-sm text-gray-500">
                  אתה עומד לשדרג לחבילה: <span className="font-semibold">{plan.name}</span> · חיוב {cycleText}
                </p>
              </div>
            </div>

            <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(displayPrice)}
                </span>
                <span className="text-sm text-gray-600">/ חודש</span>
              </div>
              <div className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p>
                  אם שמורה אמצעי תשלום ב-Stripe, ייתכן חיוב מיידי יחסי (פרורציה) עד סוף התקופה הנוכחית. חשבונית תישלח לאחר השדרוג.
                </p>
              </div>
              {currentPlanKey && (
                <p className="mt-2 text-xs text-gray-500">
                  השדרוג יתבצע מידית ויוצג בדשבורד. ניתן לבטל בכל עת דרך “ניהול חיוב”.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 md:flex-row md:justify-end">
              <button
                onClick={onClose}
                className="w-full rounded-xl border border-gray-300 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 md:w-auto"
                disabled={processing}
              >
                בטל
              </button>
              <button
                onClick={onConfirm}
                className={`w-full rounded-xl px-4 py-2 font-bold text-white md:w-auto ${
                  processing ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                }`}
                disabled={processing}
              >
                {processing ? 'מעבד…' : 'אשר שדרוג'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------- Page -------- */
function PlanUpgrade() {
  const { user, business } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string>('medium');
  const [isYearly, setIsYearly] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentPlanKey, setCurrentPlanKey] = useState<string | null>(null);
  const [currentBillingCycle, setCurrentBillingCycle] = useState<string | null>(null);
  const [coupon, setCoupon] = useState('');

  // modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPlanKey, setPendingPlanKey] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
    loadCurrentPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadPlans();
        loadCurrentPlan();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if ((event as PageTransitionEvent)?.persisted) {
        loadPlans();
        loadCurrentPlan();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow as EventListener);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const loadPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('monthly_price', { ascending: true });

      if (error) throw error;
      if (data) setPlans(data as Plan[]);
    } catch {
      toast.error('שגיאה בטעינת חבילות');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentPlan = async () => {
    if (!business?.id) return;

    try {
      // עדיפות ל-subscriptions
      const { data: subData } = await supabase
        .from('subscriptions')
        .select('plan_id, billing_cycle, subscription_plans!inner(plan_key)')
        .eq('business_id', business.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subData) {
        const planKey = (subData as any).subscription_plans?.plan_key;
        const billingCycle = (subData as any).billing_cycle;
        if (planKey) {
          setCurrentPlanKey(planKey);
          setCurrentBillingCycle(billingCycle);
          return;
        }
      }

      // fallback לטבלת businesses
      const { data: businessData } = await supabase
        .from('businesses')
        .select('plan, subscription_plans!inner(plan_key), stripe_customer_id')
        .eq('id', business.id)
        .maybeSingle();

      if (businessData) {
        const planKey = (businessData as any).subscription_plans?.plan_key;
        if (planKey) {
          setCurrentPlanKey(planKey);
          setCurrentBillingCycle(null);
        }
      }
    } catch {
      // ignore
    }
  };

  /* ------ Modal open ------ */
  const requestUpgrade = (planKey: string) => {
    if (processing) return;
    setPendingPlanKey(planKey);
    setConfirmOpen(true);
  };

  /* ------ Modal confirm ------ */
  const confirmUpgrade = async () => {
    if (!pendingPlanKey) return;
    await handleUpgrade(pendingPlanKey);
    setConfirmOpen(false);
    setPendingPlanKey(null);
  };

  /* ------ Upgrade Logic ------ */
  const handleUpgrade = async (planKey: string) => {
    if (!user?.id || !business?.id) {
      toast.error('אנא התחבר/י מחדש');
      return;
    }

    const plan = plans.find(p => p.plan_key === planKey);
    if (!plan) {
      toast.error('חבילה לא נמצאה');
      return;
    }

    const selectedBillingCycle = isYearly ? 'yearly' : 'monthly';
    if (currentPlanKey === planKey && currentBillingCycle === selectedBillingCycle) {
      toast('זו החבילה הנוכחית שלך', { icon: '✓' });
      return;
    }

    try {
      setProcessing(true);

      const priceId = isYearly ? plan.stripe_price_id_yearly : plan.stripe_price_id_monthly;
      const hasActiveSubscription = !!currentPlanKey;
      const customerId = (business as any)?.stripe_customer_id as string | undefined;

      // 1) אם הוזן קופון — נפתח Checkout כדי שהמשתמש יוכל להזין קופון/להחליף כרטיס
      if (coupon.trim().length > 0) {
        const { data, error } = await supabase.functions.invoke('PlanUpgrade', {
          body: {
            action: 'checkout',
            priceId,
            type: 'subscription',
            customerId, // חשוב כדי לא ליצור לקוח חדש
            customerEmail: user.email || '',
            metadata: {
              plan_key: planKey,
              billing_cycle: selectedBillingCycle,
              user_id: user.id,
              business_id: business.id,
              source: 'upgrade_page_with_coupon',
            },
          },
        });

        setProcessing(false);
        if (error || !data?.url) {
          toast.error('שגיאה בפתיחת דף התשלום');
          return;
        }
        window.location.href = data.url;
        return;
      }

      // 2) יש מנוי פעיל + לקוח → שדרוג Off-session עם חיוב מיידי (כולל טיפול ב-SCA)
      if (hasActiveSubscription && customerId) {
        const { data, error } = await supabase.functions.invoke('PlanUpgrade', {
          body: {
            action: 'upgrade',
            customerId,
            business_id: business.id,
            newPriceId: priceId,
            proration_behavior: 'create_prorations',
            cancel_other_actives: true,
            charge_now: true, // חיוב מיידי (פרורציה עכשיו)
          },
        });

        setProcessing(false);

        if (error || !data?.ok) {
          const msg = (error as any)?.message || (data as any)?.error || 'שגיאה בשדרוג החבילה';
          toast.error(msg);
          return;
        }

        if (data.needs_action && data.hosted_invoice_url) {
          toast('נדרש אישור תשלום. פותח דף תשלום...', { icon: '🧾' });
          window.open(data.hosted_invoice_url, '_blank');
          setTimeout(() => { loadCurrentPlan(); }, 5000);
          return;
        }

        if (data.charged_now) {
          toast.success('החבילה שודרגה וחויבה בהצלחה!');
          await loadCurrentPlan();
          return;
        }

        toast.success('החבילה שודרגה. החיוב יתבצע בחשבונית הקרובה.');
        await loadCurrentPlan();
        return;
      }

      // 3) אין מנוי פעיל/אין לקוח → Checkout רגיל (רכישה ראשונה)
      const metadata = {
        plan_key: planKey,
        billing_cycle: selectedBillingCycle,
        user_id: user.id,
        business_id: business.id,
        businessName: (business as any)?.name || '',
        source: 'upgrade_page',
      };

      const url = await createCheckoutSessionUpgrade(
        priceId,
        'subscription',
        user.email || '',
        metadata
      );

      setProcessing(false);
      window.location.href = url;
    } catch (error: any) {
      setProcessing(false);
      toast.error(error?.message || 'שגיאה בתהליך');
    }
  };

  /* ------ Helpers ------ */
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getFeaturesList = (plan: Plan): Array<{ text: string; included: boolean }> => {
    const features: Array<{ text: string; included: boolean }> = [];

    const limits = plan.limits || {};
    const maxCustomers = limits?.max_customers;
    const maxWhatsapp = limits?.max_whatsapp_messages;

    if (maxCustomers !== undefined) {
      features.push({
        text: maxCustomers === 0 || maxCustomers === -1 ? 'ללא הגבלת לקוחות' : `עד ${maxCustomers} לקוחות`,
        included: true
      });
    }

    features.push({ text: 'ללא הגבלת תורים', included: true });
    features.push({ text: 'גישה מכל מקום – מחשב ואפליקציה', included: true });
    features.push({ text: 'סטטיסטיקה ודוחות מתקדמים', included: true });

    if (plan.ai_display_limit !== null && plan.ai_display_limit > 0) {
      features.push({
        text: `עד ${plan.ai_display_limit.toLocaleString('he-IL')} פעולות AI`,
        included: true
      });
    } else if (plan.ai_display_limit === 0 || !plan.ai_display_limit) {
      features.push({ text: 'פעולות AI', included: false });
    }

    if (maxWhatsapp !== undefined && maxWhatsapp !== null) {
      if (maxWhatsapp === -1 || maxWhatsapp === 0) {
        features.push({
          text: maxWhatsapp === -1 ? 'WhatsApp ללא הגבלה' : 'הודעות WhatsApp',
          included: maxWhatsapp === -1
        });
      } else {
        features.push({
          text: `עד ${maxWhatsapp.toLocaleString('he-IL')} הודעות WhatsApp`,
          included: true
        });
      }
    }

    const isBasic = plan.plan_key === 'basic' || plan.code === 'basic';
    features.push({ text: 'תוכנית נאמנות', included: !isBasic });
    features.push({ text: 'חשבוניות וקופה', included: !isBasic });

    return features;
  };

  /* ------ Loading ------ */
  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-6xl mx-auto px-4 pt-12">
          <SkeletonLoader type="text" rows={2} className="mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SkeletonLoader type="card" rows={3} />
          </div>
        </div>
      </div>
    );
  }

  const pendingPlan = plans.find(p => p.plan_key === pendingPlanKey) || null;

  /* ------ Render ------ */
  return (
    <div dir="rtl" className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Banner חזק - חובה לבחור חבילה */}
      {!currentPlanKey && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 px-4 shadow-lg"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <Info className="h-5 w-5" />
            </div>
            <p className="text-base md:text-lg font-semibold">
              כדי להתחיל להשתמש במערכת, יש לבחור חבילה
            </p>
          </div>
        </motion.div>
      )}

      <div className="max-w-6xl mx-auto px-4 pt-8 pb-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-600">שדרג את העסק שלך</span>
          </div>

          <h1 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl">
            בחר את החבילה המתאימה לך
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            ניתן לשנות או לבטל בכל עת. חיובים מוצגים באזור החיוב והחשבוניות.
          </p>
        </motion.div>

        {/* Current plan banner */}
        {currentPlanKey && (
          <div className="mx-auto mb-8 max-w-3xl rounded-xl border border-green-200 bg-green-50 p-4 text-green-800">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <p className="text-sm">
                החבילה הנוכחית שלך: <span className="font-semibold">{currentPlanKey.toUpperCase()}</span>
                {currentBillingCycle ? ` · מחזור ${currentBillingCycle === 'yearly' ? 'שנתי' : 'חודשי'}` : ''}
              </p>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-12 flex items-center justify-center gap-6"
        >
          <button
            onClick={() => setIsYearly(false)}
            className={`rounded-lg px-6 py-2.5 font-semibold transition-all ${!isYearly ? 'scale-105 bg-blue-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            disabled={processing}
          >
            חיוב חודשי
          </button>
          <button
            onClick={() => setIsYearly(true)}
            className={`rounded-lg px-6 py-2.5 font-semibold transition-all ${isYearly ? 'scale-105 bg-blue-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            disabled={processing}
          >
            חיוב שנתי
          </button>
        </motion.div>

        {/* Coupon Input */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="max-w-md mx-auto mb-8"
        >
          <input
            dir="ltr"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value)}
            placeholder="יש לך קוד קופון? הזן כאן (אופציונלי)"
            className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-center"
          />
          <p className="text-xs text-gray-500 mt-2 text-center">
            אם מוזן קופון – פתיחת דף תשלום של Stripe (כולל שדה קופונים והחלפת כרטיס).
          </p>
        </motion.div>

        {/* Update Payment Method Button (Billing Portal) */}
        
        {/* Plans Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {plans.map((plan, index) => {
            const selectedBillingCycle = isYearly ? 'yearly' : 'monthly';
            const isCurrent = currentPlanKey === plan.plan_key && currentBillingCycle === selectedBillingCycle;
            const isPopular = plan.plan_key === 'medium';
            const monthlyPrice = plan.monthly_price;
            const yearlyPrice = plan.yearly_price;
            const avgMonthlyYearly = yearlyPrice / 12;
            const displayPrice = isYearly ? avgMonthlyYearly : monthlyPrice;
            const yearlySavings = (monthlyPrice * 12) - yearlyPrice;
            const savingsPercent = Math.round((yearlySavings / (monthlyPrice * 12)) * 100);
            const colors = planColors[plan.plan_key] || planColors.basic;
            const features = getFeaturesList(plan);

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative transition-all duration-300 ${isPopular ? 'md:scale-105' : ''}`}
              >
                <div
                  className={`relative rounded-2xl bg-white border-2 ${
                    isCurrent
                      ? 'border-green-500 shadow-xl'
                      : selected === plan.plan_key
                      ? 'border-blue-500 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                  onClick={() => setSelected(plan.plan_key)}
                >
                  {/* Popular Badge */}
                  {isPopular && !isCurrent && (
                    <div className="absolute -top-4 left-1/2 z-10 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-1.5 text-sm font-bold text-white shadow-lg">
                        <Zap className="h-4 w-4" />
                        הכי פופולרי
                      </span>
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {isCurrent && (
                    <div className="absolute -top-4 left-1/2 z-10 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-600 px-4 py-1.5 text-sm font-bold text-white shadow-lg">
                        <Shield className="h-4 w-4" />
                        החבילה הנוכחית שלך
                      </span>
                    </div>
                  )}

                  <div className={`p-8 ${isCurrent || isPopular ? 'pt-10' : ''}`}>
                    {/* Plan Header */}
                    <div className="mb-4 flex items-center gap-3">
                      <div className={`rounded-xl ${colors.accent} p-3 text-white`}>
                        {planIcons[plan.plan_key] || <Star className="h-6 w-6" />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                        <p className="text-sm text-gray-500">{plan.description}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-gray-900">{formatPrice(displayPrice)}</span>
                        <span className="text-sm text-gray-500">/ חודש</span>
                      </div>
                      {isYearly && (
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-500">
                            {formatPrice(yearlyPrice)} לשנה
                          </p>
                          {savingsPercent > 0 && (
                            <p className="flex items-center gap-1 text-sm font-semibold text-green-600">
                              <TrendingUp className="h-3 w-3" />
                              חיסכון של {formatPrice(yearlySavings)} ({savingsPercent}%)
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="mb-8 space-y-3">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          {feature.included ? (
                            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                          ) : (
                            <div className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full border-2 border-gray-300" />
                          )}
                          <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        requestUpgrade(plan.plan_key);
                      }}
                      disabled={isCurrent || processing}
                      className={`w-full rounded-xl px-6 py-3 text-base font-bold transition-all ${
                        isCurrent
                          ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                          : selected === plan.plan_key
                          ? `${colors.accent} text-white shadow-lg hover:opacity-90`
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      {processing ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          מעבד...
                        </span>
                      ) : isCurrent ? (
                        'החבילה הנוכחית'
                      ) : (
                        'בחר חבילה זו'
                      )}
                    </button>

                    {!isCurrent && (
                      <p className="mt-3 text-center text-xs text-gray-500">
                        בלחיצה על “בחר חבילה זו” תתבקש/י לאשר את השדרוג בחלון קופץ.
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="flex flex-wrap items-center justify-center gap-8 text-gray-500">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="text-sm">תשלום מאובטח</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              <span className="text-sm">ללא התחייבות</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Confirm modal */}
      <ConfirmUpgradeModal
        open={confirmOpen}
        onClose={() => {
          if (!processing) {
            setConfirmOpen(false);
            setPendingPlanKey(null);
          }
        }}
        onConfirm={confirmUpgrade}
        plan={pendingPlan}
        isYearly={isYearly}
        currentPlanKey={currentPlanKey}
        processing={processing}
      />
    </div>
  );
}

export default PlanUpgrade;
